/**
 * AddCustomerModal - Create a new customer
 *
 * Same form layout as EditCustomerModal but for creation.
 * Uses createCustomer service. Returns the new customer with ID.
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, UserPlus, Save, User, Building2 } from 'lucide-react';
import { Customer } from '@/types';
import { createCustomer, getCompanies } from '@/services/customer.service';
import { getTenantIdSync } from '@/hooks/useTenantId';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (customer: Customer) => void;
  defaultParentCompanyId?: string; // Pre-select a parent company (e.g. when adding a site from company profile)
}

const AUSTRALIAN_STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
const PROPERTY_TYPES = ['Residential', 'Commercial', 'Industrial', 'Rural', 'Government', 'Other'];
const PREFERRED_CONTACT_OPTIONS = ['Phone', 'Email', 'SMS', 'No Preference'];

const EMPTY_FORM = {
  isCompany: false,
  companyName: '',
  tradingName: '',
  abn: '',
  primaryContactName: '',
  parentCompanyId: '',
  parentCompanyName: '',
  firstName: '',
  lastName: '',
  name: '',
  phone: '',
  phoneSecondary: '',
  email: '',
  preferredContact: '',
  address: '',
  addressLine2: '',
  city: '',
  state: '',
  postcode: '',
  country: 'Australia',
  propertyType: '',
  isVip: false,
  accessInstructions: '',
  siteNotes: '',
  notes: '',
};

export function AddCustomerModal({ isOpen, onClose, onCreated, defaultParentCompanyId }: Props) {
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [companies, setCompanies] = useState<Customer[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    if (defaultParentCompanyId) {
      setForm((prev) => ({ ...prev, parentCompanyId: defaultParentCompanyId }));
    }
  }, [isOpen, defaultParentCompanyId]);

  useEffect(() => {
    if (!isOpen) return;
    if (form.isCompany) return;
    getCompanies()
      .then((list) => {
        setCompanies(list);
        if (defaultParentCompanyId && !form.parentCompanyName) {
          const match = list.find((c) => c.id === defaultParentCompanyId);
          if (match) {
            setForm((prev) => ({
              ...prev,
              parentCompanyName: match.companyName || match.name || '',
            }));
          }
        }
      })
      .catch(() => setCompanies([]));
  }, [isOpen, form.isCompany, defaultParentCompanyId]);

  const handleChange = (field: string, value: string | boolean) => {
    setForm(prev => {
      const updated = { ...prev, [field]: value };

      if (prev.isCompany) {
        if (field === 'companyName') updated.name = (value as string).trim();
      } else if (field === 'firstName' || field === 'lastName') {
        const first = field === 'firstName' ? (value as string) : prev.firstName;
        const last = field === 'lastName' ? (value as string) : prev.lastName;
        updated.name = [first, last].filter(Boolean).join(' ');
      }

      if (field === 'isCompany') {
        if (value) {
          updated.name = prev.companyName.trim() || prev.name;
          updated.parentCompanyId = '';
          updated.parentCompanyName = '';
        } else {
          updated.name = [prev.firstName, prev.lastName].filter(Boolean).join(' ') || prev.name;
        }
      }

      if (field === 'parentCompanyId') {
        const match = companies.find((c) => c.id === value);
        updated.parentCompanyName = match ? (match.companyName || match.name || '') : '';
      }

      return updated;
    });
  };

  const handleSave = async () => {
    const effectiveName = form.isCompany ? form.companyName.trim() : form.name.trim();
    if (!effectiveName) {
      setError(form.isCompany ? 'Company name is required' : 'Customer name is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const tenantId = getTenantIdSync();
      const data: Record<string, unknown> = {
        tenantId,
        name: effectiveName,
        country: form.country.trim() || 'Australia',
        source: 'Manual',
      };

      if (form.isCompany) {
        data.isCompany = true;
        data.companyName = form.companyName.trim();
        if (form.tradingName.trim()) data.tradingName = form.tradingName.trim();
        if (form.abn.trim()) data.abn = form.abn.trim();
        if (form.primaryContactName.trim()) data.primaryContactName = form.primaryContactName.trim();
      } else {
        if (form.firstName.trim()) data.firstName = form.firstName.trim();
        if (form.lastName.trim()) data.lastName = form.lastName.trim();
        if (form.parentCompanyId) {
          data.parentCompanyId = form.parentCompanyId;
          if (form.parentCompanyName) data.parentCompanyName = form.parentCompanyName;
        }
      }

      if (form.phone.trim()) data.phone = form.phone.trim();
      if (form.phoneSecondary.trim()) data.phoneSecondary = form.phoneSecondary.trim();
      if (form.email.trim()) data.email = form.email.trim();
      if (form.preferredContact) data.preferredContact = form.preferredContact;
      if (form.address.trim()) data.address = form.address.trim();
      if (form.addressLine2.trim()) data.addressLine2 = form.addressLine2.trim();
      if (form.city.trim()) data.city = form.city.trim();
      if (form.state) data.state = form.state;
      if (form.postcode.trim()) {
        data.postcode = form.postcode.trim();
        data.zipCode = form.postcode.trim();
      }
      if (form.propertyType) data.propertyType = form.propertyType;
      if (form.isVip) data.isVip = true;
      if (form.accessInstructions.trim()) data.accessInstructions = form.accessInstructions.trim();
      if (form.siteNotes.trim()) data.siteNotes = form.siteNotes.trim();
      if (form.notes.trim()) data.notes = form.notes.trim();

      const userId = localStorage.getItem('user_id') || undefined;
      const newId = await createCustomer(data as Omit<Customer, 'id' | 'createdAt' | 'updatedAt'>, userId);

      const newCustomer: Customer = {
        id: newId,
        ...data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      } as Customer;

      onCreated(newCustomer);
      setForm({ ...EMPTY_FORM });
      onClose();
    } catch (err) {
      console.error('Error creating customer:', err);
      setError('Failed to create customer. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setForm({ ...EMPTY_FORM });
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={handleClose}
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-surface border border-theme rounded-2xl shadow-2xl"
        >
          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-surface border-b border-theme">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/15 flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-theme-primary">Add Customer</h2>
                <p className="text-sm text-theme-tertiary">Create a new customer record</p>
              </div>
            </div>
            <button onClick={handleClose} className="p-2 hover:bg-surface-hover rounded-lg transition-colors">
              <X className="w-5 h-5 text-theme-muted" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-6">
            {error && (
              <div className="px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-500 dark:text-red-400">
                {error}
              </div>
            )}

            {/* Customer Type */}
            <Section title="Customer Type">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => handleChange('isCompany', false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
                    !form.isCompany
                      ? 'bg-brand-secondary/15 border-brand-secondary/50 text-theme-primary'
                      : 'bg-surface-input border-theme text-theme-tertiary hover:bg-surface-hover'
                  }`}
                >
                  <User className="w-5 h-5" />
                  <div className="text-left">
                    <div className="text-sm font-semibold">Individual</div>
                    <div className="text-[10px] opacity-70">Person / residential</div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => handleChange('isCompany', true)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
                    form.isCompany
                      ? 'bg-brand-secondary/15 border-brand-secondary/50 text-theme-primary'
                      : 'bg-surface-input border-theme text-theme-tertiary hover:bg-surface-hover'
                  }`}
                >
                  <Building2 className="w-5 h-5" />
                  <div className="text-left">
                    <div className="text-sm font-semibold">Company</div>
                    <div className="text-[10px] opacity-70">Umbrella / business</div>
                  </div>
                </button>
              </div>
              <p className="text-[10px] text-theme-muted mt-2">
                An account number will be generated automatically on save.
              </p>
            </Section>

            {/* Company-specific fields */}
            {form.isCompany && (
              <Section title="Company Details">
                <Field label="Company Name" value={form.companyName} onChange={v => handleChange('companyName', v)} placeholder="Acme Pty Ltd" required />
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Trading Name" value={form.tradingName} onChange={v => handleChange('tradingName', v)} placeholder="Trading as..." />
                  <Field label="ABN (optional)" value={form.abn} onChange={v => handleChange('abn', v)} placeholder="11-digit ABN" />
                </div>
                <Field label="Primary Contact Name" value={form.primaryContactName} onChange={v => handleChange('primaryContactName', v)} placeholder="e.g. Jane Smith" />
              </Section>
            )}

            {/* Parent Company */}
            {!form.isCompany && (
              <Section title="Parent Company">
                <div>
                  <label className="block text-xs font-medium text-theme-tertiary mb-1">
                    Link to company (optional)
                  </label>
                  <select
                    value={form.parentCompanyId}
                    onChange={e => handleChange('parentCompanyId', e.target.value)}
                    className="w-full px-3 py-2 bg-surface-input border border-theme rounded-lg text-sm text-theme-primary focus:outline-none focus:ring-1 focus:ring-brand-secondary/50 focus:border-brand-secondary/50 transition-colors"
                  >
                    <option value="">— Standalone customer —</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.companyName || c.name}
                        {c.accountNumber ? ` · ${c.accountNumber}` : ''}
                      </option>
                    ))}
                  </select>
                  {form.parentCompanyId && (
                    <p className="text-[10px] text-theme-muted mt-1">
                      Will appear as a site under <span className="font-semibold text-theme-secondary">{form.parentCompanyName}</span>
                    </p>
                  )}
                </div>
              </Section>
            )}

            {/* Personal Information — individuals only */}
            {!form.isCompany && (
              <Section title="Personal Information">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="First Name" value={form.firstName} onChange={v => handleChange('firstName', v)} placeholder="First name" />
                  <Field label="Last Name" value={form.lastName} onChange={v => handleChange('lastName', v)} placeholder="Last name" />
                </div>
                <Field label="Full Name" value={form.name} onChange={v => handleChange('name', v)} placeholder="Full name" required />
              </Section>
            )}

            {/* Contact Information */}
            <Section title="Contact Information">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Phone" value={form.phone} onChange={v => handleChange('phone', v)} placeholder="04XX XXX XXX" type="tel" />
                <Field label="Secondary Phone" value={form.phoneSecondary} onChange={v => handleChange('phoneSecondary', v)} placeholder="Optional" type="tel" />
              </div>
              <Field label="Email" value={form.email} onChange={v => handleChange('email', v)} placeholder="customer@example.com" type="email" />
              <SelectField label="Preferred Contact" value={form.preferredContact} onChange={v => handleChange('preferredContact', v)} options={PREFERRED_CONTACT_OPTIONS} placeholder="Select..." />
            </Section>

            {/* Address */}
            <Section title="Address">
              <Field label="Street Address" value={form.address} onChange={v => handleChange('address', v)} placeholder="123 Main St" />
              <Field label="Address Line 2" value={form.addressLine2} onChange={v => handleChange('addressLine2', v)} placeholder="Unit, suite, etc." />
              <div className="grid grid-cols-3 gap-4">
                <Field label="City / Suburb" value={form.city} onChange={v => handleChange('city', v)} placeholder="Sydney" />
                <SelectField label="State" value={form.state} onChange={v => handleChange('state', v)} options={AUSTRALIAN_STATES} placeholder="State" />
                <Field label="Postcode" value={form.postcode} onChange={v => handleChange('postcode', v)} placeholder="2000" />
              </div>
              <Field label="Country" value={form.country} onChange={v => handleChange('country', v)} placeholder="Australia" />
            </Section>

            {/* Site Access & Notes */}
            <Section title="Site Access & Notes">
              <TextArea label="Access Instructions" value={form.accessInstructions} onChange={v => handleChange('accessInstructions', v)} placeholder="Gate codes, parking, access instructions..." hint="Visible to technicians on job cards" />
              <TextArea label="Site Notes" value={form.siteNotes} onChange={v => handleChange('siteNotes', v)} placeholder="Dogs on property, parking limitations, etc." />
              <TextArea label="General Notes" value={form.notes} onChange={v => handleChange('notes', v)} placeholder="Any additional notes about this customer..." />
            </Section>

            {/* Classification */}
            <Section title="Classification">
              <SelectField label="Property Type" value={form.propertyType} onChange={v => handleChange('propertyType', v)} options={PROPERTY_TYPES} placeholder="Select..." />
              <label className="flex items-center gap-3 cursor-pointer group">
                <input type="checkbox" checked={form.isVip} onChange={e => handleChange('isVip', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-brand-secondary focus:ring-brand-secondary/50" />
                <span className="text-sm text-theme-secondary group-hover:text-theme-primary transition-colors">VIP Customer</span>
              </label>
            </Section>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 flex items-center justify-end gap-3 px-6 py-4 bg-surface border-t border-theme">
            <button onClick={handleClose} disabled={saving} className="px-5 py-2.5 text-sm font-medium text-theme-secondary hover:text-theme-primary hover:bg-surface-hover rounded-xl transition-colors">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || (form.isCompany ? !form.companyName.trim() : !form.name.trim())}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold rounded-xl hover:scale-[1.02] transition-transform shadow-lg shadow-emerald-500/20 disabled:opacity-50 disabled:hover:scale-100"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Creating...' : 'Create Customer'}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------
// Sub-components (same as EditCustomerModal)
// ---------------------------------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h3 className="text-[10px] font-bold text-theme-muted uppercase tracking-widest">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text', required }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string; required?: boolean }) {
  return (
    <div>
      <label className="block text-xs font-medium text-theme-tertiary mb-1">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full px-3 py-2 bg-surface-input border border-theme rounded-lg text-sm text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-1 focus:ring-brand-secondary/50 focus:border-brand-secondary/50 transition-colors" />
    </div>
  );
}

function SelectField({ label, value, onChange, options, placeholder }: { label: string; value: string; onChange: (v: string) => void; options: string[]; placeholder?: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-theme-tertiary mb-1">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} className="w-full px-3 py-2 bg-surface-input border border-theme rounded-lg text-sm text-theme-primary focus:outline-none focus:ring-1 focus:ring-brand-secondary/50 focus:border-brand-secondary/50 transition-colors">
        <option value="">{placeholder || 'Select...'}</option>
        {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    </div>
  );
}

function TextArea({ label, value, onChange, placeholder, hint }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; hint?: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-theme-tertiary mb-1">{label}</label>
      <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={3} className="w-full px-3 py-2 bg-surface-input border border-theme rounded-lg text-sm text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-1 focus:ring-brand-secondary/50 focus:border-brand-secondary/50 transition-colors resize-none" />
      {hint && <p className="text-[10px] text-theme-muted mt-1">{hint}</p>}
    </div>
  );
}
